// @effect-diagnostics globalTimers:off -- Popup observation and cancellation belong to the browser boundary.
import { CONNECT_WEB_CALLBACK_PATH } from "@afar-research/shared/connectProfile";
import type { ConnectDesktopBridge, FeedbackBridge } from "@afar-research/contracts";
import {
  makeBrowserConnect,
  browserConnectStorageKey,
  connectAccountChanged,
  refreshConnectToken,
  signInConnect,
  connectSignInTimeoutMs,
} from "@afar-research/client-runtime/connect";
import { requireConnect } from "@afar-research/shared/connect";
import {
  offersBrowserAccount,
  requiresBrowserAccount,
} from "../environments/primary/browserEntry.logic";
import { browserBuildEnvironment } from "../env";
import { APP_VERSION } from "../branding";

/**
 * The Runtime Web Client's AFAR account, and the Feedback it authenticates: what Desktop's
 * bridge provides over IPC, held here by the browser itself.
 */
export function createBrowserConnect(
  disconnect: (environmentId?: string) => void,
): { connect: ConnectDesktopBridge; feedback: FeedbackBridge } | undefined {
  const connectOrigin = import.meta.env.VITE_AFAR_CONNECT_ORIGIN;
  const clientId = import.meta.env.VITE_AFAR_CONNECT_OAUTH_CLIENT_ID;
  if (!connectOrigin || !clientId || !offersBrowserAccount(browserBuildEnvironment))
    return undefined;
  // Connect is its own OAuth issuer (ADR-0132).
  const issuer = connectOrigin;
  const accountRequired = requiresBrowserAccount(browserBuildEnvironment);
  const bridge = makeBrowserConnect({
    disconnect,
    connectOrigin,
    // Shown on the destination when the person chooses which client to revoke.
    clientLabel: navigator.platform || "Web client",
    storage: window.localStorage,
    fetch: (request) => fetch(request),
    feedbackClient: {
      clientVersion: APP_VERSION,
      platform: `web-${navigator.platform || "unknown"}`,
    },
    renewal: {
      refresh: (refreshToken) =>
        refreshConnectToken({
          issuer,
          clientId,
          refreshToken,
          fetch: (request) => fetch(request),
          now: async () => Date.now(),
        }),
      now: async () => Date.now(),
    },
    signIn: async () => {
      const popup = window.open("about:blank", "_blank", "popup,width=540,height=720");
      requireConnect(popup, "Allow popups to sign into your AFAR Product Account.");
      const redirectUri = new URL(CONNECT_WEB_CALLBACK_PATH, window.location.origin).href;
      let timer: ReturnType<typeof setInterval> | undefined;
      try {
        return await signInConnect({
          issuer,
          clientId,
          redirectUri,
          fetch: (request) => fetch(request),
          now: async () => Date.now(),
          authorize: (url) => {
            popup.location.href = url;
            const deadline = Date.now() + connectSignInTimeoutMs;
            return new Promise<string>((resolve, reject) => {
              timer = setInterval(() => {
                if (popup.closed || Date.now() >= deadline) {
                  reject(new Error("Account sign-in cancelled or expired."));
                  return;
                }
                try {
                  const callback = new URL(popup.location.href);
                  if (
                    callback.origin === window.location.origin &&
                    callback.pathname === CONNECT_WEB_CALLBACK_PATH
                  )
                    resolve(callback.href);
                } catch {
                  /* The identity page is cross-origin until it redirects back. */
                }
              }, 200);
            });
          },
        });
      } finally {
        clearInterval(timer);
        popup.close();
      }
    },
  });
  window.addEventListener("storage", (event) => {
    if (
      event.storageArea === window.localStorage &&
      (event.key === null ||
        (event.key === browserConnectStorageKey(connectOrigin) &&
          // A token another window renewed is not news here; reloading for it would drop this
          // window's connections every time any window renews.
          connectAccountChanged(event.oldValue, event.newValue)))
    ) {
      void bridge.invalidateConnections();
      if (accountRequired) window.location.reload();
    }
  });
  const { feedback, ...connect } = bridge;
  return {
    connect: {
      ...connect,
      signOut: async () => {
        await connect.signOut();
        if (accountRequired) window.location.reload();
      },
    },
    feedback,
  };
}
